const XML_PARSER_ERROR_MESSAGE = 'The Office document contains invalid XML.'

export type OoxmlXmlParent = Document | Element

export const parseOoxmlXml = (xml: string) => {
  // OOXML parts never require a DTD. Rejecting declarations before DOM parsing
  // keeps package-provided entities and external subsets out of the renderer.
  if (/<!\s*(?:DOCTYPE|ENTITY)\b/i.test(xml)) {
    throw new Error(XML_PARSER_ERROR_MESSAGE)
  }
  const document = new DOMParser().parseFromString(xml, 'application/xml')
  if (
    document.documentElement.localName === 'parsererror' ||
    document.getElementsByTagName('parsererror').length > 0
  ) {
    throw new Error(XML_PARSER_ERROR_MESSAGE)
  }
  return document
}

export const childElements = (element: Element) => {
  const children: Element[] = []
  for (
    let child = element.firstElementChild;
    child;
    child = child.nextElementSibling
  ) {
    children.push(child)
  }
  return children
}

export const childrenByName = (element: Element, localName: string) =>
  childElements(element).filter((child) => child.localName === localName)

export const firstChildByName = (
  element: Element | null | undefined,
  localName: string,
) =>
  element
    ? childElements(element).find((child) => child.localName === localName)
    : undefined

export const descendantsByName = (
  element: OoxmlXmlParent,
  localName: string,
): Element[] =>
  Array.from(element.getElementsByTagName('*')).filter(
    (child) => child.localName === localName,
  )

export const firstDescendantByName = (
  element: OoxmlXmlParent | null | undefined,
  localName: string,
) => {
  if (!element) {
    return undefined
  }
  const root =
    element.nodeType === 9
      ? (element as Document).documentElement
      : (element as Element)
  let current =
    element.nodeType === 9 ? root : (root?.firstElementChild ?? null)
  while (current) {
    if (current.localName === localName) {
      return current
    }
    if (current.firstElementChild) {
      current = current.firstElementChild
      continue
    }
    while (current !== root && !current.nextElementSibling) {
      current = current.parentElement
      if (!current) {
        return undefined
      }
    }
    if (current === root) {
      return undefined
    }
    current = current.nextElementSibling
  }
  return undefined
}

export const attributeByLocalName = (
  element: Element | null | undefined,
  localName: string,
) => {
  if (!element) {
    return null
  }
  for (const attribute of Array.from(element.attributes)) {
    if (attribute.localName === localName) {
      return attribute.value
    }
  }
  return null
}

export const numericAttribute = (
  element: Element | null | undefined,
  name: string,
  fallback = 0,
) => {
  const value = element?.getAttribute(name)
  if (value == null || value.trim() === '') {
    return fallback
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const booleanAttribute = (
  element: Element | null | undefined,
  name: string,
  fallback = false,
) => {
  const value = element?.getAttribute(name)
  if (value == null) {
    return fallback
  }
  return value === '1' || value.toLowerCase() === 'true'
}

export const textContent = (element: Element | null | undefined) =>
  element?.textContent ?? ''
